"use client";

import { ArrowLeft, ArrowRight, CheckCircle, Lock } from "lucide-react";
import { useLessonProgressOptional } from "@/components/ile/LessonProgressProvider";
import { useLessonText, useLessonLanguage } from "@/components/ile/LessonLanguageProvider";
import { lessonContentDirection } from "@/lib/lesson-language";

/**
 * Single bottom navigation bar for the lesson.
 *
 * - Non-interactive lessons: Previous (prev lesson) / Next-or-Finish, as before.
 * - Interactive (block) lessons: the section nav lives here too, so there's only
 *   one bar. Left is t("back") (previous section) once you've advanced, otherwise
 *   t("previous") (previous lesson). Right is t("continue") through the sections, and
 *   becomes Next / Finish Course on the last section.
 */
export default function LessonFooterNav({
  isBlocksLesson,
  fullWidth = false,
  hasPrev,
  onPrevious,
  nextLabel,
  onNext,
  nextDisabled,
  nextTitle,
  showQuizPassed,
  quizPassedLabel,
}: {
  isBlocksLesson: boolean;
  /** Focus mode: the lesson spans the screen, so the bar's buttons follow it to the edges. */
  fullWidth?: boolean;
  hasPrev: boolean;
  onPrevious: () => void;
  nextLabel: string;
  onNext: () => void;
  nextDisabled: boolean;
  nextTitle?: string;
  showQuizPassed: boolean;
  quizPassedLabel?: string;
}) {
  const t = useLessonText();
  const isRtl = lessonContentDirection(useLessonLanguage()) === "rtl";
  // In RTL, forward is to the LEFT. The glyphs are literal arrows, so they are chosen by
  // direction rather than mirrored by CSS.
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;
  const ForwardIcon = isRtl ? ArrowLeft : ArrowRight;

  const progress = useLessonProgressOptional();
  const interactive = Boolean(isBlocksLesson && progress);
  const interactiveReady = Boolean(interactive && progress!.sessionHydrated);

  // ── Left: section t("back") once advanced, otherwise previous-lesson t("previous") ──
  const left =
    interactiveReady && progress!.canGoBackSection ? (
      <button
        type="button"
        onClick={progress!.goBackSection}
        className="flex items-center gap-1.5 font-medium text-gray-700 hover:text-gray-900"
      >
        <BackIcon className="h-3.5 w-3.5" /> {t("back")}
      </button>
    ) : (
      <button
        type="button"
        onClick={onPrevious}
        disabled={!hasPrev}
        className={`flex items-center gap-1.5 font-medium text-gray-500 hover:text-gray-900 ${
          !hasPrev ? "cursor-not-allowed opacity-50" : ""
        }`}
      >
        <BackIcon className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t("previous")}</span>
      </button>
    );

  // ── Right: t("continue") through sections, Next/Finish on the last one ──
  let right: React.ReactNode;
  if (interactiveReady && !progress!.isLastSection) {
    const canContinue = progress!.canContinueSection;
    const { completed, total } = progress!.sectionProgress;
    const remaining = Math.max(0, total - completed);
    right = (
      <div className="flex items-center gap-3">
        {/* A greyed Continue with only a hover tooltip read as "the app is broken" to
            a learner who had just answered the visible question correctly — the
            unfinished activity was scrolled off above. Say what is outstanding, in
            text, so it works on touch too. */}
        {!canContinue && remaining > 0 ? (
          // `hidden sm:inline` used to keep this off phones — the exact devices the
          // note above is about, where the `title` tooltip does not exist either.
          // It stays in the row on mobile, capped so it wraps instead of squeezing
          // Continue off the edge.
          <span className="max-w-[9rem] text-end text-[11px] leading-tight text-slate-500 sm:max-w-none sm:text-xs">
            {remaining === 1 ? t("activityRemaining") : t("activitiesRemaining", { n: remaining })}
          </span>
        ) : null}
        <button
          type="button"
          onClick={progress!.continueSection}
          disabled={!canContinue}
          title={canContinue ? undefined : t("completeToContinue")}
          className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-bold transition-colors sm:px-4 sm:text-sm ${
            canContinue ? "bg-indigo-600 text-white hover:bg-indigo-700" : "cursor-not-allowed bg-indigo-300 text-white"
          }`}
        >
          {t("continue")} <ForwardIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  } else {
    const blockedByBlocks = Boolean(interactiveReady && !progress!.canExitLesson);
    const isDisabled = nextDisabled || blockedByBlocks;
    right = (
      <div className="flex flex-col items-end gap-1">
        {blockedByBlocks ? (
          <span className="flex items-center gap-1 text-[10px] text-amber-600">
            <Lock className="h-3 w-3" /> Complete required activities
          </span>
        ) : null}
        <button
          type="button"
          onClick={onNext}
          disabled={isDisabled}
          title={nextTitle ?? (blockedByBlocks ? t("completeRequired") : undefined)}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-bold transition-colors sm:px-4 sm:text-sm ${
            isDisabled ? "cursor-not-allowed bg-indigo-300 text-white" : "bg-indigo-600 text-white hover:bg-indigo-700"
          }`}
        >
          {nextLabel} <ForwardIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border-t border-gray-200 px-3 py-3.5 sm:px-4 sm:py-3.5 lg:px-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20 text-xs sm:text-sm">
      <div className={`${fullWidth ? "max-w-none lg:px-6" : "max-w-5xl"} mx-auto w-full flex justify-between items-center`}>
        {left}
        <div className="flex items-center gap-2 sm:gap-3">
          {showQuizPassed ? (
            <span className="text-[11px] sm:text-xs font-medium text-green-600 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />{" "}
              <span className="hidden sm:inline">{quizPassedLabel ?? t("quizPassed")}</span>
            </span>
          ) : null}
          {right}
        </div>
      </div>
    </div>
  );
}
